import React, { useState, useRef, useCallback } from 'react';
import { PaperClipIcon, XMarkIcon } from '@heroicons/react/20/solid';

/**
 * Reusable FileUploader component for handling file uploads with drag and drop
 * 
 * @param {Object} props - Component props
 * @param {Array} props.files - Array of files currently being uploaded
 * @param {Function} props.setFiles - Function to update the files array
 * @param {boolean} props.disabled - Whether the uploader is disabled
 * @param {string} props.className - Additional CSS classes for the wrapper
 * @param {Object} props.inputProps - Additional props for the file input
 * @returns {Object} Component and drag event handlers
 */
const FileUploader = ({ 
  files, 
  setFiles, 
  disabled = false, 
  className = '',
  inputProps = {} 
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  
  // Handle file input change
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setFiles(prev => [...prev, ...Array.from(e.target.files)]);
    }
  };
  
  // Open file selector
  const handleAttachClick = (e) => {
    e.preventDefault();
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  // Remove a file that's queued for upload
  const handleRemoveFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };
  
  // Handle drag events
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  }, [disabled]);
  
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !isDragging) setIsDragging(true);
  }, [isDragging, disabled]);
  
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);
  
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (disabled) return;
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setFiles(prev => [...prev, ...Array.from(e.dataTransfer.files)]);
      // Focus the textarea after dropping files
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }
  }, [setFiles, disabled]);

  // Component to render the attachment button
  const AttachmentButton = ({ showText = false }) => (
    <button
      type="button"
      onClick={handleAttachClick}
      disabled={disabled}
      className={`flex items-center ${showText ? 'py-1 px-2' : '-m-2.5 h-10 w-10 justify-center rounded-full'} text-gray-400 hover:text-gray-500`}
      title="Attach files"
    >
      <PaperClipIcon className="h-5 w-5" aria-hidden="true" />
      {showText ? (
        <span className="text-sm ml-1">Attach files or drag and drop</span>
      ) : (
        <span className="sr-only">Attach a file</span>
      )}
    </button>
  );

  // Component to render the file list
  const FileList = () => (
    files.length > 0 && (
      <div className="px-3 pb-2 mt-2">
        <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Attachments:
        </h4>
        <ul className="space-y-1">
          {files.map((file, index) => (
            <li key={index} className="flex items-center justify-between text-xs">
              <div className="flex items-center">
                <PaperClipIcon className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
                <span className="ml-1 truncate">{file.name}</span>
              </div>
              <button
                type="button"
                onClick={() => handleRemoveFile(index)}
                className="text-gray-400 hover:text-red-500"
                disabled={disabled}
              >
                <XMarkIcon className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      </div>
    )
  );
  
  // Return components and event handlers
  return {
    FileInput: () => (
      <input 
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        multiple
        accept="image/png, image/jpeg, image/gif, application/pdf"
        disabled={disabled}
        {...inputProps}
      />
    ),
    AttachmentButton,
    FileList,
    dragProps: {
      onDragEnter: handleDragEnter,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
    },
    isDragging,
    fileInputRef,
    textareaRef
  };
};

export default FileUploader;